// ============================================================
// HUBSPOT : formulaire "Demande de demonstration" en pop-up
// ============================================================
// Depuis la migration CRM vers HubSpot (Lexroom), les demandes de
// demo France passent par le formulaire HubSpot "(FRA) Website Form
// Demo" au lieu de Calendly : le contact est cree directement dans
// HubSpot avec sa source (UTM, click ids) et route vers les sales.
// Le formulaire est affiche dans une fenetre au-dessus du site, via
// l'embed officiel HubSpot (iframe). L'embed n'est charge qu'au
// premier clic pour ne pas alourdir les pages publiques.
// A la soumission, on pousse 'demo_booked' dans le dataLayer pour
// que les tags GTM deja branches sur cet evenement continuent de
// compter les demandes de demo.
// La fenetre reprend la maquette Figma « Demo popup » (charte Lexroom) :
// argumentaire et logos clients a gauche, formulaire HubSpot a droite.
// Les champs et leurs libelles se modifient dans HubSpot ; leur
// apparence (couleurs, polices, bouton, barre de progression) est
// surchargee ici via les variables CSS --hsf-default-* de l'embed.

import logoVeilJourde from "@/assets/landing-lexroom/logo-veil-jourde.png";
import logoBg2v from "@/assets/landing-lexroom/logo-bg2v.png";
import logoBredinPrat from "@/assets/landing-lexroom/logo-bredin-prat.png";
import closeIcon from "@/assets/hero-v2/close.svg";

const PORTAL_ID = "143214577";
const FORM_ID = "d93ea846-9ea1-4512-9948-9b7c4f47527f";
const REGION = "eu1";
// Embed "developer" : HubSpot injecte le formulaire directement dans
// notre page, sans iframe. Demande par Marcos (marketing) pour que GTM
// puisse suivre les interactions, impossible a travers une iframe.
const EMBED_JS =
  "https://js-eu1.hsforms.net/forms/embed/developer/" + PORTAL_ID + ".js";

const STYLE_ID = "hs-demo-modal-style";
const MODAL_ID = "hs-demo-modal";

const CLIENT_LOGOS = [
  { name: "Veil Jourde", src: logoVeilJourde },
  { name: "BG2V", src: logoBg2v },
  { name: "Bredin Prat", src: logoBredinPrat },
];

// L'offre « 3 mois offerts » ne s'affiche que sur la landing payante
// (demande de Marcos, marketing Lexroom).
const OFFER_PATH = "/landing-lexroom";

const FONT = `"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap";

const CSS = `
#${MODAL_ID} {
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(3, 30, 70, 0.6);
}
#${MODAL_ID}[hidden] { display: none; }
#${MODAL_ID} .hs-demo-card {
  position: relative; display: flex; width: 100%; max-width: 976px;
  max-height: calc(100vh - 32px); overflow-y: auto;
  background: #edf1f7; border-radius: 6px;
  box-shadow: 0 32px 71px rgba(0, 0, 0, 0.1), 0 130px 130px rgba(0, 0, 0, 0.09);
  font-family: ${FONT};
}
#${MODAL_ID} .hs-demo-pitch {
  flex: 0 0 490px; display: flex; flex-direction: column; justify-content: space-between;
  gap: 24px; padding: 40px; color: #1a1a1c;
}
#${MODAL_ID} .hs-demo-intro { display: flex; flex-direction: column; gap: 20px; }
#${MODAL_ID} .hs-demo-offer {
  display: flex; flex-direction: column; align-items: flex-start; gap: 10px;
  padding: 12px; border-radius: 12px; background: #fff;
  box-shadow: 0 2px 10px rgba(255, 255, 255, 0.8);
}
#${MODAL_ID} .hs-demo-offer[hidden] { display: none; }
#${MODAL_ID} .hs-demo-offer-pill {
  padding: 2px 8px; border-radius: 6px; background: #4d8afa; color: #fff;
  font-size: 12px; line-height: 16.8px; text-transform: uppercase;
}
#${MODAL_ID} .hs-demo-offer-text { margin: 0; font-size: 16px; line-height: 24px; color: #031e46; }
#${MODAL_ID} .hs-demo-offer-text strong { font-weight: 500; }
#${MODAL_ID} .hs-demo-copy { display: flex; flex-direction: column; gap: 12px; }
#${MODAL_ID} .hs-demo-eyebrow {
  margin: 0; font-size: 12px; line-height: 16.8px; letter-spacing: 1.2px;
  text-transform: uppercase; color: #1c73e7;
}
#${MODAL_ID} .hs-demo-title {
  margin: 0; font-size: 23px; line-height: 32.2px; font-weight: 600;
  letter-spacing: -0.115px; color: #1a1a1c;
}
#${MODAL_ID} .hs-demo-text {
  margin: 0; font-size: 14px; line-height: 21px; letter-spacing: -0.07px; color: #3f3f43;
}
#${MODAL_ID} .hs-demo-proof { display: flex; flex-direction: column; gap: 12px; }
#${MODAL_ID} .hs-demo-proof p {
  margin: 0; font-size: 12px; line-height: 24px; letter-spacing: -0.06px; color: #545458;
}
#${MODAL_ID} .hs-demo-logos {
  display: flex; align-items: center; justify-content: space-between; gap: 20px;
}
#${MODAL_ID} .hs-demo-logos img {
  width: 116px; min-width: 0; height: 46px; object-fit: contain; flex: 0 1 auto;
}
#${MODAL_ID} .hs-demo-form {
  flex: 1 1 auto; min-width: 0; padding: 60px; background: #fff; border-radius: 8px;
}
#${MODAL_ID} .hs-demo-close {
  position: absolute; top: 10px; right: 9px; z-index: 1;
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; padding: 0; border: 0; border-radius: 50%;
  background: transparent; cursor: pointer;
}
#${MODAL_ID} .hs-demo-close:hover { background: #f1f5f9; }

/* Formulaire HubSpot : variables de theme de l'embed developer */
#${MODAL_ID} .hs-form-html .hsfc-Renderer {
  --hsf-default-heading__font-family: ${FONT};
  --hsf-default-richtext__font-family: ${FONT};
  --hsf-default-button__font-family: ${FONT};
  --hsf-default-button__font-size: 14px;
  --hsf-default-button__color: #fff;
  --hsf-default-button__background-color: #1a1a1c;
  --hsf-default-button--hover__background-color: #000;
  --hsf-default-button__border-radius: 6px;
  --hsf-default-button__padding: 8px 20px;
  --hsf-default-field-label__font-family: ${FONT};
  --hsf-default-field-label__font-size: 16px;
  --hsf-default-field-label__color: #1d2027;
  --hsf-default-field-description__font-family: ${FONT};
  --hsf-default-field-description__color: #545458;
  --hsf-default-erroralert__font-family: ${FONT};
  --hsf-default-infoalert__font-family: ${FONT};
  --hsf-default-field-input__font-family: ${FONT};
  --hsf-default-field-input__background-color: #fafafa;
  --hsf-default-field-input__border-color: rgba(0, 0, 0, 0.08);
  --hsf-default-field-input__border-radius: 4px;
  --hsf-default-field-input__padding: 12px;
  --hsf-default-field-input__placeholder-color: #b7c9e2;
  --hsf-default-progressbar-text__font-family: ${FONT};
  --hsf-default-progressbar-text__color: #545458;
  --hsf-default-progressbar-progressLine__background-color: #0f4c9d;
  --hsf-default-progressbar-trackLine__background-color: #eee;
  font-family: ${FONT};
}
#${MODAL_ID} .hs-form-html .hsfc-FieldLabel { font-weight: 600; }
#${MODAL_ID} .hs-form-html .hsfc-ProgressBar__Progress,
#${MODAL_ID} .hs-form-html .hsfc-ProgressBar__Progress > div {
  height: 12px; border-radius: 20px;
}
#${MODAL_ID} .hs-form-html .hsfc-Button { font-weight: 600; flex: 1 1 0; }
#${MODAL_ID} .hs-form-html .hsfc-NavigationRow__Buttons { width: 100%; }

@media (max-width: 860px) {
  #${MODAL_ID} .hs-demo-card { flex-direction: column; }
  #${MODAL_ID} .hs-demo-pitch { flex-basis: auto; gap: 16px; padding: 28px 20px 20px; }
  #${MODAL_ID} .hs-demo-title { font-size: 20px; line-height: 28px; }
  #${MODAL_ID} .hs-demo-proof { display: none; }
  #${MODAL_ID} .hs-demo-form { padding: 20px 16px 24px; border-radius: 0; }
}
`;

let modal = null;
let lastFocus = null;

function onKeydown(e) {
  if (e.key === "Escape") closeDemoForm();
}

function buildModal() {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }
  if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = FONT_URL;
    document.head.appendChild(font);
  }

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.hidden = true;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Demande de démonstration");
  modal.innerHTML = `
    <div class="hs-demo-card">
      <button type="button" class="hs-demo-close" aria-label="Fermer">
        <img src="${closeIcon}" alt="" width="24" height="24" />
      </button>
      <div class="hs-demo-pitch">
        <div class="hs-demo-intro">
          <div class="hs-demo-offer" hidden>
            <span class="hs-demo-offer-pill">3 mois offerts</span>
            <p class="hs-demo-offer-text">Choisissez l’abonnement annuel : <strong>3 mois offerts</strong></p>
          </div>
          <div class="hs-demo-copy">
            <p class="hs-demo-eyebrow">Essayez Query gratuitement</p>
            <p class="hs-demo-title">Réservez une démo sans engagement avec l'un de nos experts.</p>
            <p class="hs-demo-text">Nous vous montrerons concrètement la plateforme et répondrons à toutes vos questions. Des tarifs sur mesure, adaptés à vos besoins.</p>
          </div>
        </div>
        <div class="hs-demo-proof">
          <p>Rejoignez plus de 300 cabinets d'avocats et directions juridiques</p>
          <div class="hs-demo-logos">
            ${CLIENT_LOGOS.map((l) => `<img src="${l.src}" alt="Logo ${l.name}" />`).join("")}
          </div>
        </div>
      </div>
      <div class="hs-demo-form">
        <div class="hs-form-html" data-region="${REGION}" data-form-id="${FORM_ID}" data-portal-id="${PORTAL_ID}"></div>
      </div>
    </div>
  `;

  modal.addEventListener("click", (e) => {
    if (e.target === modal) closeDemoForm();
  });
  modal
    .querySelector(".hs-demo-close")
    .addEventListener("click", closeDemoForm);

  document.body.appendChild(modal);

  // Le script d'embed scanne les .hs-form-html presentes au chargement :
  // on l'injecte donc apres avoir insere le conteneur dans la page.
  const script = document.createElement("script");
  script.src = EMBED_JS;
  script.defer = true;
  document.body.appendChild(script);
}

export function openDemoForm(event) {
  if (event && typeof event.preventDefault === "function")
    event.preventDefault();

  if (!modal) buildModal();
  modal.querySelector(".hs-demo-offer").hidden =
    !window.location.pathname.startsWith(OFFER_PATH);
  lastFocus = document.activeElement;
  modal.hidden = false;
  document.body.style.overflow = "hidden";
  document.addEventListener("keydown", onKeydown);
  modal.querySelector(".hs-demo-close").focus();
}

export function closeDemoForm() {
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  document.body.style.overflow = "";
  document.removeEventListener("keydown", onKeydown);
  if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
}

let trackingReady = false;

function pushDemoBooked() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "demo_booked" });
}

export function initDemoFormTracking() {
  if (trackingReady) return;
  trackingReady = true;

  // Embed HubSpot actuel (hs-form-html) : evenement DOM sur window.
  window.addEventListener("hs-form-event:on-submission:success", pushDemoBooked);

  // Ancien embed HubSpot (hbspt.forms.create) : message postMessage.
  window.addEventListener("message", (e) => {
    if (
      e.data &&
      e.data.type === "hsFormCallback" &&
      e.data.eventName === "onFormSubmitted" &&
      e.data.id === FORM_ID
    ) {
      pushDemoBooked();
    }
  });
}
